import { useState } from 'react'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  CsvTableViewer,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import {
  downloadCloudAccrualExport,
  openCloudAccrualReport,
  useCloudAccrualAgentReview,
  useCloudAccrualHealth,
  useCloudAccrualRun,
} from './api'
import type {
  AgentReview,
  CloudAccrualCategory,
  CloudAccrualHealth,
  CloudAccrualRun,
  CloudAccrualView,
  CloudCommitment,
  CloudUsageItem,
} from './types'
import { formatCurrency, formatPct, statusBadgeClass, varianceClass } from './utils'

export default function CloudAccrualsPage({ view = 'overview' }: { view?: CloudAccrualView }) {
  const { data, isLoading, error } = useCloudAccrualRun()
  const { data: health } = useCloudAccrualHealth()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading cloud accruals...
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-[1180px] space-y-4">
        <DataSourceStrip health={health} />
        <Panel>
          <PanelBody>
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Cloud Accruals data could not be loaded from Redshift.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (view === 'review') return <ReviewView run={data} health={health} />
  if (view === 'sources') return <SourcesView run={data} health={health} />
  if (view === 'exports') return <ExportsView run={data} health={health} />
  return <OverviewView run={data} health={health} />
}

function OverviewView({ run, health }: { run: CloudAccrualRun; health?: CloudAccrualHealth }) {
  return (
    <div className="mx-auto max-w-[1500px] space-y-6">
      <DataSourceStrip health={health} />
      <DecisionStrip run={run} />

      <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-6 max-xl:grid-cols-1">
        <CategoryLedger categories={run.categories} />
        <ExceptionRail run={run} />
      </div>

      <div className="grid grid-cols-2 gap-6 max-2xl:grid-cols-1">
        <UsageTable rows={run.usage_items} />
        <CommitmentsTable rows={run.commitments} />
      </div>
    </div>
  )
}

function DataSourceStrip({ health }: { health?: CloudAccrualHealth }) {
  if (!health) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge variant="info">Checking Redshift</Badge>
          <Badge variant="info">Checking Langfuse</Badge>
        </div>
      </div>
    )
  }

  const redshiftVariant = health.redshift_connected ? 'success' : 'danger'
  const langfuseVariant = health.langfuse_enabled ? 'success' : 'warning'
  const modeLabel = health.mode === 'saved'
    ? 'Saved connector'
    : health.mode === 'env_fallback'
      ? 'Env fallback'
      : 'Unconfigured'

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={redshiftVariant}>
            Redshift {health.redshift_connected ? 'connected' : 'not connected'}
          </Badge>
          <Badge variant="secondary">{modeLabel}</Badge>
          {health.redshift_schema && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              schema {health.redshift_schema}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={langfuseVariant}>
            Langfuse {health.langfuse_enabled ? 'enabled' : 'inactive'}
          </Badge>
          {health.langfuse_project_id && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {health.langfuse_project_id}
            </span>
          )}
        </div>
      </div>
      {health.message && (
        <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
          {health.message}
        </p>
      )}
    </div>
  )
}

function DecisionStrip({ run }: { run: CloudAccrualRun }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
      <div className="grid grid-cols-[1.25fr_0.75fr_0.75fr_0.75fr] max-lg:grid-cols-2 max-sm:grid-cols-1">
        <div className="border-r border-gray-200 p-5 dark:border-dark-border max-sm:border-r-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Posting decision
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
            <div className="text-3xl font-semibold tracking-normal text-charcoal dark:text-gray-100">
              {formatCurrency(run.summary.grand_total, true)}
            </div>
            <Badge variant="warning">{run.summary.hitl_required_count} review gates</Badge>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
            {run.summary.alert}
          </p>
        </div>
        <DecisionMetric label="Threshold" value={formatCurrency(run.summary.total_threshold, true)} />
        <DecisionMetric label="Variance gate" value={`${Math.round(run.summary.variance_threshold * 100)}%`} />
        <DecisionMetric label="NetSuite files" value={run.summary.netsuite_ready ? 'Ready' : 'Blocked'} />
      </div>
    </div>
  )
}

function DecisionMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-r border-gray-200 p-5 last:border-r-0 dark:border-dark-border max-lg:border-t max-lg:last:border-r">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function CategoryLedger({ categories }: { categories: CloudAccrualCategory[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Provider accrual ledger</PanelTitle>
        <PanelDescription>
          Accrual, variance, owner, and posting accounts by cloud cost family.
        </PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 text-right font-semibold">Accrual</th>
              <th className="px-4 py-3 text-right font-semibold">Prior avg</th>
              <th className="px-4 py-3 text-right font-semibold">Variance</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Owner</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {categories.map(category => (
              <tr key={category.id} className="align-top">
                <td className="px-5 py-4">
                  <div className="font-semibold text-charcoal dark:text-gray-100">
                    {category.label}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {category.provider} - DR {category.debit_account} / CR {category.credit_account}
                  </div>
                </td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(category.amount)}
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(category.prior_average)}
                </td>
                <td className={cn('px-4 py-4 text-right font-semibold tabular-nums', varianceClass(category.variance_pct))}>
                  {formatCurrency(category.variance_amount)}
                  <div className="text-xs">{formatPct(category.variance_pct)}</div>
                </td>
                <td className="px-4 py-4">
                  <span className={statusBadgeClass(category.status)}>
                    {category.status === 'hitl' ? 'HITL' : 'Auto'}
                  </span>
                  {category.hitl_reason && (
                    <p className="mt-2 max-w-[280px] text-xs leading-5 text-gray-500 dark:text-gray-400">
                      {category.hitl_reason}
                    </p>
                  )}
                </td>
                <td className="px-4 py-4 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  {category.owner}
                  <div>{category.detail_count} evidence rows</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ExceptionRail({ run }: { run: CloudAccrualRun }) {
  const reviewRows = run.usage_items.filter(item => item.needs_review)
  const weakCommitment = run.commitments.find(row => row.utilization_pct < 80)
  const largest = [...run.categories].sort((a, b) => b.amount - a.amount)[0]
  return (
    <div className="space-y-4">
      <Panel>
        <PanelHeader>
          <PanelTitle className="text-lg">Controller focus</PanelTitle>
          <PanelDescription>Items most likely to change posting readiness.</PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-5">
          <FocusItem label="Largest accrual" value={largest ? largest.provider : '-'} detail={largest ? `${largest.label}: ${formatCurrency(largest.amount)}` : '-'} />
          <FocusItem label="Usage exceptions" value={`${reviewRows.length} rows`} detail="Medium-confidence or spike-driven usage needs owner approval." />
          <FocusItem label="Commitment risk" value={weakCommitment ? weakCommitment.provider : 'Clear'} detail={weakCommitment ? `${weakCommitment.commitment_id} at ${weakCommitment.utilization_pct.toFixed(1)}% utilization.` : 'All commitments are above the review threshold.'} />
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>
          <PanelTitle className="text-lg">Export status</PanelTitle>
        </PanelHeader>
        <PanelBody className="space-y-3 text-sm">
          <ExportRow label="Journal entry" status={run.exports.journal_entry.balanced} rows={run.exports.journal_entry.rows} />
          <ExportRow label="Reversal" status={run.exports.reversal.balanced} rows={run.exports.reversal.rows} />
        </PanelBody>
      </Panel>
    </div>
  )
}

function FocusItem({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</span>
        <span className="font-semibold text-charcoal dark:text-gray-100">{value}</span>
      </div>
      <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{detail}</p>
    </div>
  )
}

function ExportRow({ label, status, rows }: { label: string; status: boolean; rows: number }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      <span className={cn('inline-flex items-center gap-1 font-medium', status ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300')}>
        {status ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
        {status ? 'Balanced' : 'Blocked'} - {rows} rows
      </span>
    </div>
  )
}

function UsageTable({ rows }: { rows: CloudUsageItem[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Usage evidence</PanelTitle>
        <PanelDescription>Provider exports and unbilled usage through close cutoff.</PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3">Provider</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Cost center</th>
              <th className="px-4 py-3 text-right">Unbilled</th>
              <th className="px-4 py-3">Confidence</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={`${row.provider}-${row.service_name}-${row.account_id}`}>
                <td className="px-5 py-4 font-medium text-charcoal dark:text-gray-100">{row.provider}</td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-300">
                  {row.service_name}
                  <div className="text-xs text-gray-500">{row.account_id}</div>
                </td>
                <td className="px-4 py-4 text-gray-600 dark:text-gray-300">{row.cost_center}</td>
                <td className="px-4 py-4 text-right font-semibold tabular-nums text-charcoal dark:text-gray-100">
                  {formatCurrency(row.unbilled_amount)}
                </td>
                <td className="px-4 py-4">
                  <Badge variant={row.needs_review ? 'warning' : 'secondary'}>
                    {row.confidence}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function CommitmentsTable({ rows }: { rows: CloudCommitment[] }) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader>
        <PanelTitle>Commitment coverage</PanelTitle>
        <PanelDescription>Savings plans, reservations, and committed use discount evidence.</PanelDescription>
      </PanelHeader>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
              <th className="px-5 py-3">Commitment</th>
              <th className="px-4 py-3 text-right">Monthly</th>
              <th className="px-4 py-3 text-right">Utilization</th>
              <th className="px-4 py-3 text-right">Unused</th>
              <th className="px-4 py-3">Owner</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {rows.map(row => (
              <tr key={row.commitment_id}>
                <td className="px-5 py-4">
                  <div className="font-medium text-charcoal dark:text-gray-100">{row.commitment_id}</div>
                  <div className="text-xs text-gray-500">{row.provider} - {row.description}</div>
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.monthly_commitment)}
                </td>
                <td className={cn('px-4 py-4 text-right font-semibold tabular-nums', row.utilization_pct < 80 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300')}>
                  {row.utilization_pct.toFixed(1)}%
                </td>
                <td className="px-4 py-4 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatCurrency(row.unused_amount)}
                </td>
                <td className="px-4 py-4 text-xs text-gray-500 dark:text-gray-400">{row.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function ReviewView({ run, health }: { run: CloudAccrualRun; health?: CloudAccrualHealth }) {
  const review = useCloudAccrualAgentReview()
  const [lastReview, setLastReview] = useState<AgentReview | null>(null)

  const runReview = (useLlm: boolean) => {
    review.mutate(
      { use_llm: useLlm },
      { onSuccess: result => setLastReview(result) },
    )
  }

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <DataSourceStrip health={health} />
      <Panel>
        <PanelHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <PanelTitle>Controller review</PanelTitle>
            <PanelDescription>
              Langfuse-traced review of cloud usage completeness, commitment exceptions, and export readiness.
            </PanelDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => runReview(false)} disabled={review.isPending}>
              {review.isPending ? <ArrowPathIcon className="animate-spin" /> : <ShieldCheckIcon />}
              Deterministic Review
            </Button>
            <Button variant="brand" onClick={() => runReview(true)} disabled={review.isPending}>
              {review.isPending ? <ArrowPathIcon className="animate-spin" /> : <DocumentTextIcon />}
              LLM Review
            </Button>
          </div>
        </PanelHeader>
        <PanelBody className="space-y-5">
          <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
            <ReviewMetric label="Accrual total" value={formatCurrency(run.summary.grand_total)} />
            <ReviewMetric label="Review gates" value={String(run.summary.hitl_required_count)} />
            <ReviewMetric label="Usage exceptions" value={String(run.usage_items.filter(item => item.needs_review).length)} />
            <ReviewMetric label="Trace schema" value={run.data_source.redshift_schema} />
          </div>

          {review.error && (
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Review failed: {review.error.message}
            </p>
          )}

          {lastReview ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{lastReview.mode}</Badge>
                  {lastReview.langfuse_trace_id && (
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      trace {lastReview.langfuse_trace_id}
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm leading-6 text-gray-700 dark:text-gray-200">
                  {lastReview.summary}
                </p>
              </div>
              <div className="grid gap-3">
                {lastReview.findings.map((finding, index) => (
                  <div key={`${finding.title}-${index}`} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={finding.severity === 'blocker' ? 'danger' : finding.severity === 'warning' ? 'warning' : 'secondary'}>
                        {finding.severity}
                      </Badge>
                      <span className="font-semibold text-charcoal dark:text-gray-100">{finding.title}</span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{finding.detail}</p>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Recommended actions</h3>
                <ul className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  {lastReview.recommended_actions.map(action => (
                    <li key={action} className="flex gap-2">
                      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Run a review to create a Langfuse trace and produce controller findings.
            </p>
          )}
        </PanelBody>
      </Panel>
    </div>
  )
}

function ReviewMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-2 truncate text-lg font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  )
}

function SourcesView({ run, health }: { run: CloudAccrualRun; health?: CloudAccrualHealth }) {
  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <DataSourceStrip health={health} />
      <div className="grid grid-cols-[360px_minmax(0,1fr)] gap-6 max-xl:grid-cols-1">
        <Panel>
          <PanelHeader>
            <PanelTitle>Source files</PanelTitle>
            <PanelDescription>Evidence loaded into Redshift for this close run.</PanelDescription>
          </PanelHeader>
          <PanelBody className="space-y-3">
            {run.source_files.map(file => (
              <div key={file.filename} className="rounded-lg border border-gray-200 p-3 dark:border-dark-border">
                <div className="font-medium text-charcoal dark:text-gray-100">{file.filename}</div>
                <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{file.description}</p>
              </div>
            ))}
            <Button variant="outline" onClick={openCloudAccrualReport}>
              <DocumentTextIcon />
              Open Source Report
            </Button>
          </PanelBody>
        </Panel>
        <UsageTable rows={run.usage_items} />
      </div>
    </div>
  )
}

function ExportsView({ run, health }: { run: CloudAccrualRun; health?: CloudAccrualHealth }) {
  const journalRows = exportPreviewRows(run)
  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <DataSourceStrip health={health} />
      <div className="grid grid-cols-2 gap-6 max-lg:grid-cols-1">
        <ExportPanel
          label="Journal entry"
          status={run.exports.journal_entry}
          onDownload={() => downloadCloudAccrualExport('je')}
        />
        <ExportPanel
          label="Reversal"
          status={run.exports.reversal}
          onDownload={() => downloadCloudAccrualExport('reversal')}
        />
      </div>
      <Panel>
        <PanelHeader>
          <PanelTitle>Journal preview</PanelTitle>
          <PanelDescription>Debit lines generated from Redshift category rows.</PanelDescription>
        </PanelHeader>
        <PanelBody>
          <CsvTableViewer
            filename="cloud-accruals-preview.csv"
            pageSize={8}
            columns={[
              { id: 'journal_number', header: 'Journal Number', minWidth: '160px' },
              { id: 'account_number', header: 'Account', width: '100px' },
              { id: 'account_name', header: 'Account Name', minWidth: '180px' },
              { id: 'debit', header: 'Debit', align: 'right', minWidth: '120px' },
              { id: 'credit', header: 'Credit', align: 'right', minWidth: '120px' },
              { id: 'department', header: 'Department', minWidth: '180px' },
              { id: 'source', header: 'Source', minWidth: '200px' },
            ]}
            rows={journalRows}
          />
        </PanelBody>
      </Panel>
    </div>
  )
}

function ExportPanel({
  label,
  status,
  onDownload,
}: {
  label: string
  status: CloudAccrualRun['exports']['journal_entry']
  onDownload: () => void
}) {
  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>{label}</PanelTitle>
        <PanelDescription>{status.filename}</PanelDescription>
      </PanelHeader>
      <PanelBody className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <ReviewMetric label="Rows" value={String(status.rows)} />
          <ReviewMetric label="Debits" value={formatCurrency(status.debit_total)} />
          <ReviewMetric label="Credits" value={formatCurrency(status.credit_total)} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge variant={status.balanced ? 'success' : 'danger'}>
            {status.balanced ? 'Balanced' : 'Blocked'}
          </Badge>
          <Button variant="brand" onClick={onDownload} disabled={!status.balanced}>
            <ArrowDownTrayIcon />
            Download CSV
          </Button>
        </div>
      </PanelBody>
    </Panel>
  )
}

function exportPreviewRows(run: CloudAccrualRun) {
  return run.categories.map(category => ({
    journal_number: 'JE-2025-04-CLOUD001',
    account_number: category.debit_account,
    account_name: `${category.provider} Cloud Expense`,
    debit: formatCurrency(category.amount),
    credit: '',
    department: category.owner,
    source: category.label,
  }))
}
